import React, { useEffect, useMemo } from 'react';
import { Button } from '@vtbhome/juicyfront';
import { Body, Footer } from './modal-edit-file.styles';
import { Field, Form } from 'react-final-form';
import { EditFormKeys } from '../../config/form-constants';
import { validate } from '../../lib/form-validate';
import { Typography } from '@shared/ui/typography/typography';
import { InputText } from '@shared/ui/input-text/input-text';
import { InputNumber } from '@shared/ui/input-number/input-number';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { NetworkStateNew } from '@shared/ui/network-state-new/network-state-new';
import { EditFormFields, ModalEditFileProps } from './types';
import { useFileDetailSharedQuery } from '@entities/file/libs/hooks/use-file-detail-shared-query/use-file-detail-shared-query';
import { CACHE_KEY_FILE_EDIT } from '../../config/api-constants';
import { useFileEditMutation } from '../../api/file-edit-api';
import { InputFile } from '@shared/ui/input-file/input-file';
import { InputFileValue } from '@shared/ui/input-file/types';
import { Checkbox } from '@shared/ui/checkbox/checkbox';
import { DISK_OPTIONS } from '../../config/ui-constants';
import { Select } from '@shared/ui/select/select';
import { Modal } from '@shared/ui/modal/modal';
import { useLazyPrivateImageQuery } from '@entities/file/api/file-api';
import { checkFileIsImage } from '@features/file/create-file/lib/check-file-is-image';
import { useResetModalForm } from '@shared/lib/hooks/use-reset-modal-form/use-reset-modal-form';

export const ModalEditFile: React.FC<ModalEditFileProps> = ({ isShow, file, onClose, onAccept }) => {
    const formRef = useResetModalForm<Partial<EditFormFields>, Partial<Partial<EditFormFields>>>(isShow);
    const [getFileDetail, getFileDetailResult] = useFileDetailSharedQuery(file.id);
    const [editFile, editFileResult] = useFileEditMutation({
        fixedCacheKey: CACHE_KEY_FILE_EDIT,
    });
    const [getPrivateImage, getPrivateImageResult] = useLazyPrivateImageQuery();
    const fileDetail = getFileDetailResult.data;
    const error = getQueryError(getFileDetailResult);
    const isLoadingDetail =
        getFileDetailResult.isFetching || getFileDetailResult.isLoading || getFileDetailResult.isUninitialized;
    const isLoadingEdit = editFileResult.isLoading;

    const handleSubmit = async (fields: EditFormFields) => {
        const fieldFile = fields[EditFormKeys.UploadedFile].localFiles[0]?.file || undefined;
        const result = await editFile({
            id: file.id,
            body: {
                disk: fields[EditFormKeys.Disk][0].value,
                name: fields[EditFormKeys.Name],
                code: fields[EditFormKeys.Code],
                path: fields[EditFormKeys.Path],
                sort: fields[EditFormKeys.Sort],
                auth: !fields[EditFormKeys.Auth],
                uploadedFile: fieldFile,
            },
        });
        if (!result.error) {
            onAccept();
        }
    };

    const initialFiles = useMemo(() => {
        let fileUrl = fileDetail?.url || '';
        const isImage = checkFileIsImage(file.meta.extension);
        const isPrivate = file.meta.hidden;
        if (isPrivate) {
            if (isImage && !getPrivateImageResult.data) {
                getPrivateImage(file.id);
            }
            if (getPrivateImageResult.data) {
                fileUrl = getPrivateImageResult.data;
            }
        }

        return {
            uploadedFiles: [
                {
                    id: fileDetail?.id,
                    url: fileUrl,
                    name: fileDetail?.name || '',
                    size: fileDetail?.size || '',
                    extension: file.meta.extension,
                },
            ],
            localFiles: [],
        } as InputFileValue;
    }, [fileDetail]);

    const initialDisk = useMemo(() => {
        const option = DISK_OPTIONS.find((option) => option.value === fileDetail?.disk);
        return option && [option];
    }, [fileDetail]);

    useEffect(() => {
        if (isShow && file.id) {
            getFileDetail(file.id);
        }
    }, [file.id, isShow]);

    return (
        <Form
            onSubmit={handleSubmit}
            validate={validate}
            render={(form) => {
                formRef.current = form;
                return (
                    <Modal
                        isShow={isShow}
                        variant='drawer'
                        size='l'
                        showLine={false}
                        header={<Typography size='h6'>Редактирование файла</Typography>}
                        footer={
                            <Footer>
                                <Button disabled={isLoadingEdit} buttonType='light' onClick={onClose}>
                                    Отменить
                                </Button>
                                <Button
                                    preloader={isLoadingEdit}
                                    disabled={isLoadingEdit}
                                    buttonType='primary'
                                    onClick={form.handleSubmit}>
                                    Подтвердить
                                </Button>
                            </Footer>
                        }
                        onClose={onClose}>
                        <Body>
                            <NetworkStateNew
                                size='s'
                                isLoading={isLoadingDetail}
                                isError={Boolean(error)}
                                isInitialized={Boolean(fileDetail)}
                                isFiltered={false}
                                hasData={Boolean(fileDetail)}
                                errorProps={{
                                    title: 'Ошибка загрузки организации',
                                    description: error?.message,
                                }}>
                                <Field
                                    name={EditFormKeys.UploadedFile}
                                    initialValue={initialFiles}
                                    render={({ input, meta }) => {
                                        return (
                                            <InputFile
                                                name={EditFormKeys.UploadedFile}
                                                disabled={isLoadingEdit}
                                                label='Файл в формате PDF, DOC, DOCX, XLS, XLSX, JPEG, JPG, PNG'
                                                value={input.value}
                                                error={meta.touched && meta.error}
                                                placeholder='Загрузить файл'
                                                accept='.doc,.docx,.pdf,.xls,.xlsx,.jpeg,.jpg,.png'
                                                count={1}
                                                required
                                                onChange={input.onChange}
                                            />
                                        );
                                    }}
                                />
                                <Field
                                    name={EditFormKeys.Name}
                                    initialValue={fileDetail?.name}
                                    render={({ input, meta }) => (
                                        <InputText
                                            required
                                            disabled={isLoadingEdit}
                                            label='Название файла'
                                            placeholder='Лицензионное соглашение'
                                            value={input.value || ''}
                                            error={meta.touched && meta.error}
                                            onChange={input.onChange}
                                        />
                                    )}
                                />
                                <Field
                                    name={EditFormKeys.Code}
                                    initialValue={fileDetail?.code}
                                    render={({ input, meta }) => (
                                        <InputText
                                            disabled={isLoadingEdit}
                                            required
                                            label='Системное название (код)'
                                            placeholder='license-agreement'
                                            value={input.value || ''}
                                            error={meta.touched && meta.error}
                                            mask={/[^a-z0-9_-]/gi}
                                            onChange={input.onChange}
                                        />
                                    )}
                                />
                                <Field
                                    name={EditFormKeys.Path}
                                    initialValue={fileDetail?.path}
                                    render={({ input, meta }) => (
                                        <InputText
                                            required
                                            disabled={isLoadingEdit}
                                            startValue='/'
                                            label='Относительный путь'
                                            placeholder='/directory'
                                            value={input.value || '/'}
                                            error={meta.touched && meta.error}
                                            mask={/[^a-z0-9/_-]/gi}
                                            onChange={input.onChange}
                                        />
                                    )}
                                />
                                <Field
                                    name={EditFormKeys.Disk}
                                    initialValue={initialDisk}
                                    render={({ input, meta }) => {
                                        return (
                                            <Select
                                                disabled={isLoadingEdit}
                                                required
                                                label='Диск'
                                                placeholder='Выберите диск'
                                                values={input.value}
                                                options={DISK_OPTIONS}
                                                error={meta.touched && meta.error}
                                                onChange={input.onChange}
                                            />
                                        );
                                    }}
                                />
                                <Field
                                    name={EditFormKeys.Sort}
                                    initialValue={fileDetail?.sort === undefined ? '0' : String(fileDetail.sort)}
                                    render={({ input, meta }) => (
                                        <InputNumber
                                            {...input}
                                            disabled={isLoadingEdit}
                                            label='Сортировка'
                                            placeholder='Введите число'
                                            hint='Чем меньше число, тем выше файл в списке'
                                            error={meta.touched && meta.error}
                                        />
                                    )}
                                />
                                <Field
                                    type='checkbox'
                                    name={EditFormKeys.Auth}
                                    defaultValue={!fileDetail?.auth}
                                    render={({ input }) => (
                                        <Checkbox
                                            label='Получение файла без авторизации'
                                            name={EditFormKeys.Auth}
                                            checked={input.checked}
                                            disabled={isLoadingEdit}
                                            onChange={input.onChange}
                                        />
                                    )}
                                />
                            </NetworkStateNew>
                        </Body>
                    </Modal>
                );
            }}
        />
    );
};
